import { useEffect, useState } from "react";

import { getSearchTemplates } from "@/api/endpoints/searches";
import ConfirmModal from "@/containers/ConfirmModal";
import { useMessages } from "@/containers/Messages/MessageContext";
import { SearchQuery } from "@/types/SearchQuery";
import { SearchQueryTemplate } from "@/types/Template";
import { cx } from "@/utils";
import Button from "@components/Button";
import { Input, Select } from "@components/Field";
import StaffBadge from "@components/StaffBadge";
import Typography from "@components/Typography";

import styles from "./FromTemplate.module.css";

export type FromTemplateProps = {
  onSet: (k: SearchQuery[]) => void;
  disabled?: boolean;
  warn?: boolean;
};

const removeUuid = (queries: SearchQuery[]) =>
  queries.map(({ uuid: _uuid, ...rest }) => rest);

const FromTemplate: React.FunctionComponent<FromTemplateProps> = ({
  onSet,
  disabled,
  warn = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<SearchQueryTemplate[]>([]);
  const [active, setActive] = useState<number | string>("");
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Set the queryset using onSet, show a modal if changes has been made to the search
  const onApply = () => {
    if (active) {
      if (warn) {
        setModalOpen(true);
      } else {
        onSet(templates[Number(active)].queries);
      }
    }
  };

  useEffect(() => {
    getSearchTemplates()
      .then((response) => {
        if (response.status !== 200) {
          throw response;
        }
        if (response.data) {
          setTemplates(response.data);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.data?.detail || true);
      });
  }, []);

  const { danger } = useMessages();

  const [pastedSearch, setPastedSearch] = useState<string>("");

  const apply = () => {
    try {
      const parsed = JSON.parse(pastedSearch) as { queries: SearchQuery[] };
      const parsedSearchQueries = parsed?.queries;
      const parsedSearchQueriesWithoutUuid = removeUuid(parsedSearchQueries);
      onSet(parsedSearchQueriesWithoutUuid as SearchQuery[]);
    } catch {
      danger({ title: "Invalid JSON" });
    }
  };

  return (
    <div className={cx(styles.root, { [styles.disabled]: disabled })}>
      <Typography variant="h6">
        <StaffBadge /> {L.MENTIONS_QUERYBUILDER_TEMPLATE_TITLE}
      </Typography>
      <fieldset className={styles.fieldset} disabled={loading || disabled}>
        <Select
          error={Boolean(error)}
          block
          className={styles.input}
          placeholder={
            error || L.MENTIONS_QUERYBUILDER_TEMPLATE_SELECT_PLACEHOLDER
          }
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setActive(e.target.value)
          }
          value={active}
          data-testid="test-search-template-select"
          id="search-template-select"
        >
          {templates.map(({ title }, i) => (
            <option key={i} value={i}>
              {title}
            </option>
          ))}
        </Select>
        <Button
          disabled={loading || active === ""}
          onClick={onApply}
          data-testid="apply-search-template"
        >
          {L.MENTIONS_QUERYBUILDER_TEMPLATE_APPLY}
        </Button>
      </fieldset>
      <br />

      <Input
        block
        onChange={(e) => setPastedSearch(e.currentTarget.value)}
        placeholder={L.QB_POWER_MODE_JSON_PLACEHOLDER}
      />
      <Button disabled={!pastedSearch} onClick={apply} className="mt-4">
        {L.QB_POWER_MODE_CTA}
      </Button>

      <ConfirmModal
        message={L.MENTIONS_QUERYBUILDER_TEMPLATE_OVERRIDE_WARNING}
        title={L.MENTIONS_QUERYBUILDER_TEMPLATE_TITLE}
        show={modalOpen}
        onCancel={() => {
          setModalOpen(false);
        }}
        onConfirm={() => {
          onSet(templates[Number(active)].queries);
          setModalOpen(false);
        }}
      />
    </div>
  );
};

export default FromTemplate;
